import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { createOrder, updateOrder } from '../../../services/ApiService';
import { useSystemTables } from '../../../hooks/UseSystemTables';
import Spinner from '../../../components/Spinner/Spinner';
import {
  Box,
  IconButton,
  Button,
  TextField,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import styles from './CreateOrder.module.css';
import { ApiError } from '../../../models/ApiError';
import { defaultErrorHandler } from '../../../services/ErrorHandler';
import { Client } from '../../../models/SystemTables';

const CreateOrder: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine page direction using i18n.dir() if available
  const isRTL = i18n.dir ? i18n.dir() === 'rtl' : i18n.language === 'he';

  const [formData, setFormData] = useState({
    itemNum: '',
    origin: '',
    amount: '',
    amountRestriction: false,
    isSelfShipping: false,
    requestedTime: '',
    clientInnerId: '',
    tonAmount: '',
    transportationCompany: '',
    comment: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>('');

  // Check if we're in edit mode
  const isEditMode = location.state?.isEdit === true;
  const orderId = location.state?.orderId as string | undefined;

  // Handle pre-filled data from navigation state
  useEffect(() => {
    const preFilledData = location.state?.preFilledData;
    if (preFilledData) {
      setFormData(prev => ({
        ...prev,
        ...preFilledData,
      }));
    }
  }, [location.state]);

  const {
    data: systemTablesData,
    isLoading: tablesLoading,
    isError: tablesError,
  } = useSystemTables();

  // Prepare options for select fields
  const itemNumOptions =
    systemTablesData?.data?.genericCatalog?.map((cat) => ({
      value: cat.genericItemNumber,
      label: cat.genericItemNumber,
    })) || [];

  // Get unique clientInnerId values from underlingUsers
  const allowedClientInnerIds = new Set(
    systemTablesData?.data?.underlingUsers?.map(
      (user) => user.attributes['custom:clientInnerId']
    ) || []
  );

  // Filter clients to only those associated with underlingUsers
  const underlingUsersOptions =
    systemTablesData?.data?.clients
      ?.filter((client: Client) => allowedClientInnerIds.has(client.clientInnerId))
      .map((client: Client) => {
        const displayName = client.clientName || client.clientInnerId;
        const label = `${displayName} (${client.clientInnerId})`;
        return {
          value: client.clientInnerId,
          label: label,
        };
      }) || [];

  const originOptions =
    systemTablesData?.data?.warehouseLocations?.map((location) => ({
      value: location._id,
      label: i18n.language === 'he' ? location?.nameHeb : location?.nameEng,
    })) || [];

  const transportationCompanyOptions =
    systemTablesData?.data?.transportCompanies?.map((transportCompany) => ({
      value: transportCompany.companyInnerId,
      label: transportCompany.companyName
        ? transportCompany.companyName
        : transportCompany.companyInnerId,
    })) || [];

  const createOrderMutation = useMutation({
    mutationFn: createOrder,
    onSuccess: (data) => {
      console.log('Order created successfully:', data);
      setFormErrors({});
      setGeneralError('');
      navigate('/order-history');
    },
    onError: (error: any) => {
      console.error('Error creating order:', error);
      
      // Handle error using ErrorHandler
      if (ApiError.isApiError(error)) {
        // Extract field-specific errors for form validation
        const fieldErrors = defaultErrorHandler.extractFieldErrors(error);
        
        if (Object.keys(fieldErrors).length > 0) {
          // Set field-specific errors
          setFormErrors(fieldErrors);
          setGeneralError('');
        } else {
          // Set general error message
          const message = defaultErrorHandler.getUserFriendlyMessage(error);
          setGeneralError(message);
          setFormErrors({});
        }
      } else {
        // Fallback for non-ApiError
        const apiError = defaultErrorHandler.handle(error);
        const message = defaultErrorHandler.getUserFriendlyMessage(apiError);
        setGeneralError(message);
        setFormErrors({});
      }
    },
  });

  const updateOrderMutation = useMutation({
    mutationFn: updateOrder,
    onSuccess: (data) => {
      console.log('Order updated successfully:', data);
      setFormErrors({});
      setGeneralError('');
      navigate('/order-history');
    },
    onError: (error: any) => {
      console.error('Error updating order:', error);
      
      // Handle error using ErrorHandler
      if (ApiError.isApiError(error)) {
        // Extract field-specific errors for form validation
        const fieldErrors = defaultErrorHandler.extractFieldErrors(error);
        
        if (Object.keys(fieldErrors).length > 0) {
          // Set field-specific errors
          setFormErrors(fieldErrors);
          setGeneralError('');
        } else {
          // Set general error message
          const message = defaultErrorHandler.getUserFriendlyMessage(error);
          setGeneralError(message);
          setFormErrors({});
        }
      } else {
        // Fallback for non-ApiError
        const apiError = defaultErrorHandler.handle(error);
        const message = defaultErrorHandler.getUserFriendlyMessage(apiError);
        setGeneralError(message);
        setFormErrors({});
      }
    },
  });

  // Handler for TextField and TextArea inputs
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handler for Autocomplete components
  const handleAutocompleteChange = (name: string) => (
    event: React.SyntheticEvent,
    newValue: { value: string; label: string } | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newValue?.value || '',
    }));
    // Clear field error when user selects a value
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Helper function to get the selected option object from value string
  const getSelectedOption = (
    value: string,
    options: { value: string; label: string }[]
  ): { value: string; label: string } | null => {
    return options.find((option) => option.value === value) || null;
  };

  // Handler for Checkbox inputs
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Validate required fields before submission
  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.clientInnerId) {
      errors.clientInnerId = t('Login.Errors.EmptyInput', { InputLabel: t('CreateOrder.Labels.UnderlingUsers') });
    }

    if (!formData.itemNum) {
      errors.itemNum = t('Login.Errors.EmptyInput', { InputLabel: t('Order.ItemNum') });
    }

    if (!formData.origin) {
      errors.origin = t('Login.Errors.EmptyInput', { InputLabel: t('Order.Origin') });
    }

    // Amount validation: either amount or tonAmount must be filled based on amountRestriction
    if (formData.amountRestriction) {
      if (!formData.tonAmount || formData.tonAmount.trim() === '') {
        errors.tonAmount = t('Login.Errors.EmptyInput', { InputLabel: t('CreateOrder.Labels.TonAmount') });
      }
    } else {
      if (!formData.amount || formData.amount.trim() === '') {
        errors.amount = t('Login.Errors.EmptyInput', { InputLabel: t('Order.Amount') });
      }
    }

    if (!formData.requestedTime) {
      errors.requestedTime = t('Login.Errors.EmptyInput', { InputLabel: t('Order.RequestedTime') });
    }

    // Conditional required field: transportationCompany when isSelfShipping is true
    if (formData.isSelfShipping && !formData.transportationCompany) {
      errors.transportationCompany = t('Login.Errors.EmptyInput', { InputLabel: t('Order.TransportationCompany') });
    }

    return errors;
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors({});
    setGeneralError('');

    // Validate form before submission
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    if (isEditMode && orderId) {
      // Convert requestedTime from datetime-local to ISO string for update
      const orderData = {
        orderId: orderId,
        itemNum: formData.itemNum,
        clientInnerId: formData.clientInnerId,
        origin: formData.origin,
        amount: formData.amount,
        isSelfShipping: formData.isSelfShipping,
        requestedTime: formData.requestedTime,
        transportationCompany: formData.transportationCompany || undefined,
        tonAmount: formData.tonAmount || undefined,
        comment: formData.comment || undefined,
      };
      updateOrderMutation.mutate(orderData);
    } else {
      // Convert requestedTime from datetime-local to ISO string for create
      const orderData = {
        ...formData,
        requestedTime: formData.requestedTime ? new Date(formData.requestedTime).toISOString() : '',
      };
      createOrderMutation.mutate(orderData);
    }
  };

  const handleReturnClick = () => {
    navigate('/order-history');
  };

  const handleErrorClose = () => {
    setGeneralError('');
  };

  return (
    <div className={styles.container}>
      {/* Header with centered title and dynamic back button positioning */}
      <Box display="flex" alignItems="center" justifyContent="center" position="relative" mb={2}>
        <IconButton
          size="small"
          onClick={handleReturnClick}
          sx={{
            position: 'absolute',
            [isRTL ? 'right' : 'left']: 0,
            p: 0,
            minWidth: 0,
            width: 24,
            height: 24,
          }}
        >
          <ArrowBackIcon
            fontSize="inherit"
            sx={{
              transform: isRTL ? 'scaleX(-1)' : 'none',
            }}
          />
        </IconButton>
        <Typography variant="h5">{isEditMode ? t('CreateOrder.EditTitle') : t('CreateOrder.Title')}</Typography>
      </Box>

      {((isEditMode ? updateOrderMutation.isPending : createOrderMutation.isPending) || tablesLoading) && <Spinner />}
      {tablesError && (
        <Typography color="error">{t('CreateOrder.Errors.SystemTables')}</Typography>
      )}

      {!tablesLoading && !tablesError && (
        <form onSubmit={handleFormSubmit}>
          {/* General Error Message */}
          {generalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {generalError}
            </Alert>
          )}
          {/* Underling Users (clientInnerId) */}
          <Autocomplete
            fullWidth
            options={underlingUsersOptions}
            value={getSelectedOption(formData.clientInnerId, underlingUsersOptions)}
            onChange={handleAutocompleteChange('clientInnerId')}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('CreateOrder.Labels.UnderlingUsers')}
                margin="normal"
                error={!!formErrors.clientInnerId}
                helperText={formErrors.clientInnerId}
              />
            )}
          />

          {/* Item Number */}
          <Autocomplete
            fullWidth
            options={itemNumOptions}
            value={getSelectedOption(formData.itemNum, itemNumOptions)}
            onChange={handleAutocompleteChange('itemNum')}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('Order.ItemNum')}
                margin="normal"
                error={!!formErrors.itemNum}
                helperText={formErrors.itemNum}
              />
            )}
          />

          {/* Origin */}
          <Autocomplete
            fullWidth
            options={originOptions}
            value={getSelectedOption(formData.origin, originOptions)}
            onChange={handleAutocompleteChange('origin')}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('Order.Origin')}
                margin="normal"
                error={!!formErrors.origin}
                helperText={formErrors.origin}
              />
            )}
          />

          {/* Amount */}
          <TextField
            fullWidth
            margin="normal"
            name="amount"
            label={t('Order.Amount')}
            type="number"
            value={formData.amountRestriction ? '1' : formData.amount}
            onChange={handleInputChange}
            disabled={formData.amountRestriction}
            error={!!formErrors.amount}
            helperText={formErrors.amount}
          />

          {/* Amount Restriction Checkbox */}
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  name="amountRestriction"
                  checked={formData.amountRestriction}
                  onChange={(e) => {
                    handleCheckboxChange(e);
                    if (e.target.checked) {
                      setFormData(prev => ({ ...prev, amount: '1' }));
                    }
                  }}
                />
              }
              label={t('CreateOrder.Labels.AmountRestriction')}
            />
          </FormGroup>

          {/* Conditional Ton Amount */}
          {formData.amountRestriction && (
            <TextField
              fullWidth
              margin="normal"
              name="tonAmount"
              label={t('CreateOrder.Labels.TonAmount')}
              type="number"
              value={formData.tonAmount}
              onChange={handleInputChange}
              error={!!formErrors.tonAmount}
              helperText={formErrors.tonAmount}
            />
          )}

          {/* Requested Time */}
          <TextField
            fullWidth
            margin="normal"
            name="requestedTime"
            label={t('Order.RequestedTime')}
            type="datetime-local"
            value={formData.requestedTime}
            onChange={handleInputChange}
            error={!!formErrors.requestedTime}
            helperText={formErrors.requestedTime}
            InputLabelProps={{
              shrink: true,
            }}
          />

          {/* Self Shipping Checkbox */}
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  name="isSelfShipping"
                  checked={formData.isSelfShipping}
                  onChange={handleCheckboxChange}
                />
              }
              label={t('Order.IsSelfShipping.Title')}
            />
          </FormGroup>

          {/* Conditional Transportation Company */}
          {formData.isSelfShipping && (
            <Autocomplete
              fullWidth
              options={transportationCompanyOptions}
              value={getSelectedOption(formData.transportationCompany, transportationCompanyOptions)}
              onChange={handleAutocompleteChange('transportationCompany')}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('Order.TransportationCompany')}
                  margin="normal"
                  error={!!formErrors.transportationCompany}
                  helperText={formErrors.transportationCompany}
                />
              )}
            />
          )}

          {/* Comment */}
          <TextField
            fullWidth
            margin="normal"
            name="comment"
            label={t('Order.Comment')}
            type="text"
            value={formData.comment}
            onChange={handleInputChange}
          />

          {/* Submit Button */}
          <Box mt={2}>
            <Button 
              variant="contained" 
              color="primary" 
              type="submit" 
              fullWidth
              disabled={isEditMode ? updateOrderMutation.isPending : createOrderMutation.isPending}
            >
              {(isEditMode ? updateOrderMutation.isPending : createOrderMutation.isPending) ? t('General.Labels.Submitting') || 'Submitting...' : t('CreateOrder.SendOrder')}
            </Button>
          </Box>
        </form>
      )}

      {/* Error Snackbar for quick notifications */}
      <Snackbar
        open={!!generalError && !(isEditMode ? updateOrderMutation.isPending : createOrderMutation.isPending)}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
          {generalError}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CreateOrder;